import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Button, CircularProgress, Paper, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { SupplierBillService } from '../../services/supplierBill.service';
import { SupplierBill, BillEditDTO } from '../../interfaces/procurement/supplierBill.interface';
import { useToast } from '../../context/ToastContext';

export const SupplierBillEdit: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const toast = useToast();

  const [bill, setBill] = useState<SupplierBill | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [billDate, setBillDate] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [supplierInvoiceRef, setSupplierInvoiceRef] = useState('');
  const [taxAmount, setTaxAmount] = useState<number>(0);
  const [editReason, setEditReason] = useState('');
  // Per-line editable unit price keyed by goodsReceiptLineId
  const [prices, setPrices] = useState<Record<string, number>>({});

  useEffect(() => {
    if (!id) return;
    SupplierBillService.getById(id)
      .then(b => {
        setBill(b);
        setBillDate(b.billDate);
        setDueDate(b.dueDate);
        setSupplierInvoiceRef(b.supplierInvoiceRef ?? '');
        setTaxAmount(b.taxAmount ?? 0);
        const initialPrices: Record<string, number> = {};
        b.lines.forEach(l => {
          initialPrices[l.goodsReceiptLineId] = l.unitPrice;
        });
        setPrices(initialPrices);
      })
      .catch(e => toast.showApiError(e, 'Failed to load supplier bill'))
      .finally(() => setLoading(false));
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSubmit = async () => {
    if (!bill) return;
    if (editReason.trim().length === 0) {
      toast.showApiError(null, 'A reason is required');
      return;
    }
    if (bill.lines.some(l => !((prices[l.goodsReceiptLineId] ?? l.unitPrice) > 0))) {
      toast.showApiError(null, 'Enter a positive bill unit price for every line');
      return;
    }
    setSaving(true);
    try {
      const dto: BillEditDTO = {
        billDate,
        dueDate,
        supplierInvoiceRef: supplierInvoiceRef || undefined,
        editReason: editReason.trim(),
        taxAmount: taxAmount || undefined,
        lines: bill.lines.map(l => ({
          goodsReceiptLineId: l.goodsReceiptLineId,
          unitPrice: prices[l.goodsReceiptLineId] ?? l.unitPrice,
        })),
      };
      const updated = await SupplierBillService.edit(bill.id, dto);
      toast.showSuccess('Bill updated');
      navigate(`/procurement/supplier-bills/${updated.id}`);
    } catch (e) {
      toast.showApiError(e, 'Failed to update bill');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <Box sx={{ p: 3 }}><CircularProgress /></Box>;
  if (!bill) return <Box sx={{ p: 3 }}><Typography>Supplier bill not found.</Typography></Box>;

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h5" sx={{ mb: 3 }}>Edit Supplier Bill — {bill.billNumber}</Typography>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <TextField
            label="Bill Date"
            type="date"
            value={billDate}
            onChange={e => setBillDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
          />

          <TextField
            label="Due Date"
            type="date"
            value={dueDate}
            onChange={e => setDueDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            required
          />

          <TextField
            label="Supplier Invoice Ref"
            value={supplierInvoiceRef}
            onChange={e => setSupplierInvoiceRef(e.target.value)}
          />

          <TextField
            label="Tax Amount"
            type="number"
            value={taxAmount}
            onChange={e => setTaxAmount(Number(e.target.value))}
            inputProps={{ step: 0.01, min: 0 }}
          />

          <TextField
            label="Reason for edit"
            value={editReason}
            onChange={e => setEditReason(e.target.value)}
            multiline
            minRows={2}
            required
            error={editReason.trim().length === 0 && editReason.length > 0}
            helperText="Required — explain why this bill is being edited"
          />
        </Box>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>Bill Lines</Typography>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Line ID</TableCell>
              <TableCell>GRN Line ID</TableCell>
              <TableCell>Item ID</TableCell>
              <TableCell>Quantity</TableCell>
              <TableCell>PO Unit Price</TableCell>
              <TableCell>Bill Unit Price</TableCell>
              <TableCell>Line Value</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {bill.lines.map(line => {
              const rawPrice = prices[line.goodsReceiptLineId] ?? line.unitPrice;
              const billPrice = Number.isFinite(rawPrice) ? rawPrice : NaN;
              return (
                <TableRow key={line.id}>
                  <TableCell>{line.id}</TableCell>
                  <TableCell>{line.goodsReceiptLineId}</TableCell>
                  <TableCell>{line.itemId}</TableCell>
                  <TableCell>{line.quantity}</TableCell>
                  <TableCell>{line.poUnitPrice ?? '—'}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      error={!(billPrice > 0)}
                      value={Number.isFinite(billPrice) ? billPrice : ''}
                      onChange={e => {
                        const v = e.target.value;
                        setPrices(prev => ({
                          ...prev,
                          [line.goodsReceiptLineId]: v === '' ? NaN : Number(v),
                        }));
                      }}
                      inputProps={{ step: 0.0001, min: 0, 'aria-label': `bill-unit-price-${line.goodsReceiptLineId}` }}
                      sx={{ width: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    {Number.isFinite(billPrice) ? (billPrice * line.quantity).toFixed(4) : '—'}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2 }}>
        <Button variant="contained" onClick={handleSubmit} disabled={saving}>
          {saving ? <CircularProgress size={20} /> : 'Save Changes'}
        </Button>
        <Button variant="outlined" onClick={() => navigate(`/procurement/supplier-bills/${bill.id}`)} disabled={saving}>
          Cancel
        </Button>
      </Box>
    </Box>
  );
};

export default SupplierBillEdit;
